import React from 'react';
import { DataTable } from '../../common/DataTable';

// Customer requests captured before any job exists.
//
// The aging column is the follow-up mechanism. There is no reminder job behind
// this feature -- an inquiry going stale has to be visible, or it is a paper
// notepad with extra steps.

const DAY_MS = 24 * 60 * 60 * 1000;

export const formatAge = (isoString, now = new Date()) => {
  if (!isoString) return '—';
  const then = new Date(isoString);
  if (Number.isNaN(then.getTime())) return '—';

  const days = Math.floor((now - then) / DAY_MS);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks} weeks ago`;
};

// A 'converted' row whose converted_job_id is NULL is one whose job was
// deleted (the FK is ON DELETE SET NULL). The backend goes out of its way to
// keep exactly that row usable -- see _RECONVERTIBLE_OR_CLOSEABLE in
// inquiry_service.py -- because there is no job left to protect. The UI used
// to render it as "Job #null" with no action, which made a recovery the
// service deliberately supports unreachable.
export const isOrphanedConversion = (row) =>
  row.status === 'converted' && row.converted_job_id == null;

// Open (not yet chased), closed (a customer who went quiet can call back) and
// orphaned conversions can all become a job.
const canConvert = (row) =>
  row.status === 'open' || row.status === 'closed' || isOrphanedConversion(row);

const STATUSES = [
  ['open', 'Open'],
  ['converted', 'Became a job'],
  ['closed', 'Closed'],
];

const InquiriesTab = ({
  inquiries,
  isLoading,
  inquiryStatus,
  setInquiryStatus,
  inquiriesPage,
  setInquiriesPage,
  inquiriesTotalPages,
  onConvert,
  onCloseInquiry,
  // The hook's overlay-scoped flag. Row actions are disabled while a
  // mutation is in flight: nginx proxies /api/ with a 30s timeout, so a slow
  // convert looks like a dead tap and invites a second one -- on the action
  // that creates a job. Deliberately NOT the shared `isLoading` (#52/#96):
  // this disables the trigger, it does not unmount it, so Modal.js still has
  // a live node to restore focus to.
  actionsBusy = false,
}) => {
  const columns = [
    {
      header: 'Customer',
      accessor: 'customer_name',
      render: (row) => (
        <div className="flex flex-col leading-tight">
          <span className="font-medium text-ink">{row.customer_name}</span>
          <span className="text-small text-ink-muted">{row.customer_phone}</span>
        </div>
      ),
    },
    {
      header: 'Vehicle',
      accessor: 'model',
      render: (row) => (
        <span className="text-ink-soft">
          {`${row.year} ${row.make} ${row.model}`}
        </span>
      ),
    },
    {
      header: 'Request',
      accessor: 'inquiry_notes',
      render: (row) =>
        row.inquiry_notes ? (
          <span className="text-ink-soft">{row.inquiry_notes}</span>
        ) : (
          <span className="text-ink-muted">—</span>
        ),
    },
    {
      header: 'Waiting',
      accessor: 'created_at',
      render: (row) => (
        <span className="text-ink-soft">{formatAge(row.created_at)}</span>
      ),
    },
    {
      header: '',
      accessor: 'id',
      render: (row) =>
        canConvert(row) ? (
          <div className="flex items-center gap-2">
            {isOrphanedConversion(row) && (
              <span className="text-small text-ink-muted">Job deleted</span>
            )}
            <button
              type="button"
              onClick={() => onConvert(row)}
              disabled={actionsBusy}
              className="min-h-touch rounded-card bg-accent px-3 py-2 text-white hover:bg-accent-hover disabled:opacity-60"
            >
              Convert to job
            </button>
            {row.status === 'open' && (
              <button
                type="button"
                onClick={() => onCloseInquiry(row.id)}
                disabled={actionsBusy}
                className="min-h-touch rounded-card px-3 py-2 text-ink-soft disabled:opacity-60"
              >
                Close
              </button>
            )}
          </div>
        ) : (
          <span className="text-small text-ink-muted">
            {`Job #${row.converted_job_id}`}
          </span>
        ),
    },
  ];

  // Mechanics read this on a phone; DataTable swaps to cards below sm.
  const renderCard = (row) => (
    <div className="rounded-card border border-border-default bg-surface-card p-4">
      <div className="flex items-baseline justify-between">
        <span className="font-medium text-ink">{row.customer_name}</span>
        <span className="text-small text-ink-muted">{formatAge(row.created_at)}</span>
      </div>
      <div className="text-ink-soft">{`${row.year} ${row.make} ${row.model}`}</div>
      <div className="text-small text-ink-muted">{row.customer_phone}</div>
      {row.inquiry_notes && (
        <p className="mt-2 text-ink-soft">{row.inquiry_notes}</p>
      )}
      {isOrphanedConversion(row) && (
        <p className="mt-2 text-small text-ink-muted">Job deleted</p>
      )}
      {canConvert(row) && (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={() => onConvert(row)}
            disabled={actionsBusy}
            className="min-h-touch flex-1 rounded-card bg-accent px-3 py-3 text-white hover:bg-accent-hover disabled:opacity-60"
          >
            Convert to job
          </button>
          {row.status === 'open' && (
            <button
              type="button"
              onClick={() => onCloseInquiry(row.id)}
              disabled={actionsBusy}
              className="min-h-touch rounded-card px-3 py-3 text-ink-soft disabled:opacity-60"
            >
              Close
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <label htmlFor="inquiry-status" className="text-small text-ink-soft">
          Show
        </label>
        <select
          id="inquiry-status"
          value={inquiryStatus}
          onChange={(event) => setInquiryStatus(event.target.value)}
          className="rounded-card border border-border-strong bg-surface-card px-3 py-2 text-ink"
        >
          {STATUSES.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        data={inquiries}
        columns={columns}
        loading={isLoading}
        density="compact"
        pagination={false}
        filterable={false}
        sortable={false}
        emptyMessage="No inquiries yet. Use “+ New inquiry” to capture one."
        renderCard={renderCard}
      />

      {inquiriesTotalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-ink">
            {`Page ${inquiriesPage} of ${inquiriesTotalPages}`}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={inquiriesPage <= 1}
              onClick={() => setInquiriesPage(inquiriesPage - 1)}
              className="min-h-touch rounded-card px-3 py-2 text-ink-soft disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={inquiriesPage >= inquiriesTotalPages}
              onClick={() => setInquiriesPage(inquiriesPage + 1)}
              className="min-h-touch rounded-card px-3 py-2 text-ink-soft disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InquiriesTab;
